import React from 'react';
import { ExternalLink, ChevronRight, BarChart3, Table, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import ReasoningTrace from './ReasoningTrace';
import ActionPreview from './ActionPreview';
import OpportunityCreationFlow from './OpportunityCreationFlow';

/**
 * Rich Message Renderer for AI Chat
 * Handles different response types: text, table, cards, mixed, conversational_flow, action_preview
 * Now includes ReasoningTrace for audit transparency
 */
const RichMessageRenderer = ({
  message,
  onFollowUpSelect,
  onNavigate,
  onSendMessage,
  onActionConfirm,
  onActionCancel,
  onFlowComplete,
  isLoading = false
}) => {
  const {
    content,
    response_type = 'text',
    structured_data,
    action_links,
    follow_up_suggestions,
    tool_results,
    flow_data,
    action_preview
  } = message;

  // Parse markdown-like formatting
  const formatContent = (text) => {
    if (!text) return null;
    
    // Split by newlines and handle lists
    const lines = text.split('\n');
    
    return lines.map((line, idx) => {
      // Handle bullet points
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ') || line.trim().startsWith('• ')) {
        return (
          <li key={idx} className="ml-4 text-sm text-gray-700">
            {formatInlineText(line.trim().substring(2))}
          </li>
        );
      }
      // Handle bold headers with **
      if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
        return (
          <p key={idx} className="font-semibold text-gray-900 mt-2 mb-1">
            {line.trim().replace(/\*\*/g, '')}
          </p>
        );
      }
      // Regular line
      if (line.trim()) {
        return (
          <p key={idx} className="text-sm text-gray-700 mb-1">
            {formatInlineText(line)}
          </p>
        );
      }
      return <br key={idx} />;
    });
  };

  // Handle inline formatting (bold, numbers, percentages)
  const formatInlineText = (text) => {
    // Bold text between **
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      // Highlight numbers and percentages
      const highlighted = part.replace(
        /(\$[\d,]+\.?\d*[MKB]?|\d+\.?\d*%|\d{1,3}(?:,\d{3})*)/g,
        '<span class="font-medium text-[#07689f]">$1</span>'
      );
      return <span key={idx} dangerouslySetInnerHTML={{ __html: highlighted }} />;
    });
  };

  // Check for conversational flow response
  const hasConversationalFlow = response_type === 'conversational_flow' && tool_results?.some(
    tr => tr.result?.response_type === 'conversational_flow'
  );

  // Check for action preview response
  const hasActionPreview = response_type === 'action_preview' || tool_results?.some(
    tr => tr.result?.response_type === 'action_preview'
  );

  // Get flow data from tool results
  const getFlowData = () => {
    if (flow_data) return flow_data;
    const flowResult = tool_results?.find(tr => tr.result?.response_type === 'conversational_flow');
    return flowResult?.result || null;
  };

  // Get action preview data from tool results
  const getActionPreviewData = () => {
    if (action_preview) return action_preview;
    const previewResult = tool_results?.find(tr => tr.result?.response_type === 'action_preview');
    if (previewResult?.result) {
      const r = previewResult.result;
      return {
        entity: r.preview?.entity ? {
          type: r.preview.entity.type || 'Entity',
          name: r.preview.entity.display_name || 'Unknown',
          context: r.preview.entity.context ? Object.values(r.preview.entity.context).filter(Boolean).join(' • ') : null
        } : null,
        changes: r.preview?.changes?.map(c => ({
          field: c.field_label || c.field,
          oldValue: c.old_display || c.old_value,
          newValue: c.new_display || c.new_value,
          highlight: c.field === 'program_name' || c.field === 'stage'
        })) || [],
        confirmationRequired: true,
        reasonOptions: r.preview?.reasoning_options?.map(o => o.label) || [],
        warnings: r.preview?.warnings || [],
        actionType: r.action_type,
        actionParams: r.action_params
      };
    }
    return null;
  };

  const flowDataResolved = getFlowData();
  const actionPreviewData = getActionPreviewData();

  return (
    <div className="space-y-3">
      {/* Main Content */}
      <div className="prose prose-sm max-w-none">
        {formatContent(content)}
      </div>

      {/* Conversational Flow - Create Opportunity */}
      {hasConversationalFlow && flowDataResolved?.flow_state?.flow_type === 'create_opportunity' && (
        <div className="mt-3">
          <OpportunityCreationFlow
            flowData={flowDataResolved}
            onComplete={onFlowComplete}
            onCancel={onActionCancel}
            onSendMessage={onSendMessage}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Action Preview */}
      {hasActionPreview && actionPreviewData && (
        <div className="mt-3">
          <ActionPreview
            actionData={actionPreviewData}
            onConfirm={(reason) => onActionConfirm?.(actionPreviewData.actionType, actionPreviewData.actionParams, reason)}
            onCancel={onActionCancel}
          />
        </div>
      )}

      {/* Table Data */}
      {structured_data?.table_data && structured_data.table_data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
            <Table className="h-3 w-3" />
            <span>Data Table</span>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {structured_data.columns?.map((col, idx) => (
                  <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {structured_data.table_data.slice(0, 5).map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-gray-50">
                  {structured_data.columns?.map((col, colIdx) => (
                    <td key={colIdx} className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {typeof row[col] === 'number' 
                        ? row[col].toLocaleString()
                        : row[col] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {structured_data.table_data.length > 5 && (
            <p className="text-xs text-gray-400 mt-1">
              Showing 5 of {structured_data.table_data.length} rows
            </p>
          )}
        </div>
      )}

      {/* Summary Stats */}
      {structured_data?.summary && (
        <div className="bg-[#e8f4fa] border border-[#b8dff5] rounded-lg p-3 mt-2">
          <div className="flex items-center gap-2 mb-1 text-xs text-[#07689f]">
            <BarChart3 className="h-3 w-3" />
            <span className="font-medium">Summary</span>
          </div>
          <div className="text-sm text-[#04526f]">
            {formatContent(structured_data.summary)}
          </div>
        </div>
      )}

      {/* Action Links */}
      {action_links && action_links.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {action_links.map((link, idx) => (
            <button
              key={idx}
              onClick={() => onNavigate?.(link.url)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#07689f] bg-[#e8f4fa] hover:bg-[#d1ebf7] rounded-full transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              {link.label}
            </button>
          ))}
        </div>
      )}

      {/* Reasoning Trace - Audit Trail */}
      {tool_results && tool_results.length > 0 && (
        <ReasoningTrace toolResults={tool_results} />
      )}

      {/* Follow-up Suggestions */}
      {follow_up_suggestions && follow_up_suggestions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Follow-up questions:</p>
          <div className="flex flex-wrap gap-2">
            {follow_up_suggestions.slice(0, 3).map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => onFollowUpSelect?.(suggestion)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                {suggestion}
                <ChevronRight className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RichMessageRenderer;
